import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { safeReturnPath } from '../lib/navigation'
import Logo from '../components/Logo'
import MfaForm from '../components/MfaForm'
import { useAuth } from '../lib/useAuth'

/**
 * Lands here after GitHub OAuth. The control plane puts the token pair in the
 * URL fragment, which — unlike the query string — is never sent to a server or
 * written into an access log. An account with a second factor gets a challenge
 * token in the same place instead, and the code prompt comes up here.
 */
export default function AuthCallback() {
  const { adopt, completeMfa } = useAuth()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [mfaToken, setMfaToken] = useState<string | null>(null)

  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.slice(1))
    const access = params.get('access_token')
    const refresh = params.get('refresh_token')
    const challenge = params.get('mfa_token')

    // Whatever arrived, drop it out of the address bar before anything else.
    window.history.replaceState(null, '', '/auth/callback')

    if (challenge) {
      setMfaToken(challenge)
      return
    }
    if (!access || !refresh) {
      setError('The sign-in response was incomplete. Please try again.')
      return
    }

    void (async () => {
      try {
        await adopt({
          access_token: access,
          refresh_token: refresh,
          token_type: 'bearer',
          expires_in: 3600,
        })
        navigate(safeReturnPath(sessionStorage.getItem('talaricloud.returnTo')), { replace: true }); sessionStorage.removeItem('talaricloud.returnTo')
      } catch {
        setError('Could not complete sign-in. Please try again.')
      }
    })()
  }, [adopt, navigate])

  if (mfaToken) {
    return (
      <div className="auth">
        <div className="auth-card">
          <Logo size={44} className="auth-logo" />
          <MfaForm
            onSubmit={async (code) => {
              await completeMfa(mfaToken, code)
              navigate(safeReturnPath(sessionStorage.getItem('talaricloud.returnTo')), { replace: true }); sessionStorage.removeItem('talaricloud.returnTo')
            }}
            onCancel={() => navigate('/login', { replace: true })}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="auth">
      <div className="card auth-card" style={{ textAlign: 'center' }}>
        {error ? (
          <>
            <div className="alert error">{error}</div>
            <a className="btn primary" href="/login">
              Back to sign in
            </a>
          </>
        ) : (
          <>
            <span className="spinner" />
            <p className="muted" style={{ marginTop: 12 }}>
              Finishing sign-in…
            </p>
          </>
        )}
      </div>
    </div>
  )
}

